import { useEffect, useState } from 'react';

// Extension du global window pour inclure l'API iubenda
declare global {
    interface Window {
        /* eslint-disable @typescript-eslint/naming-convention */
        _iub?: {
            cs?: {
                api?: {
                    isConsentGiven?: () => boolean;
                    getPreferences?: () => Record<string, unknown>;
                };
            };
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }
}

export interface CookieConsentState {
    isAccepted: boolean;
    isLoading: boolean;
}

export function useCookieConsent(): CookieConsentState {
    const [state, setState] = useState<CookieConsentState>(() => {
        // Determine initial state synchronously to avoid flash
        if (typeof window === 'undefined') {
            return { isAccepted: true, isLoading: false };
        }

        // Quick synchronous check for initial state
        const cookieString = document.cookie;
        const iubendaCookieMatch = cookieString.match(/_iub_cs-s?\d+=([^;]+)/);

        if (iubendaCookieMatch && iubendaCookieMatch[1]) {
            try {
                const cookieValue = decodeURIComponent(iubendaCookieMatch[1]);
                const consentData = JSON.parse(cookieValue);
                const hasVideoConsent =
                    consentData.purposes && consentData.purposes['3'] === true;

                return { isAccepted: hasVideoConsent, isLoading: false };
            } catch (error) {
                // If parsing fails, start with loading state
                return { isAccepted: false, isLoading: true };
            }
        }

        // No cookie found - user hasn't been asked yet, show content
        return { isAccepted: true, isLoading: false };
    });

    useEffect(() => {
        let isMounted = true;

        const checkCookieConsent = () => {
            if (!isMounted || typeof window === 'undefined') {
                return;
            }

            const cookieString = document.cookie;

            // Look for iubenda cookie
            const iubendaCookieMatch = cookieString.match(
                /_iub_cs-s?\d+=([^;]+)/,
            );

            if (iubendaCookieMatch && iubendaCookieMatch[1]) {
                try {
                    const cookieValue = decodeURIComponent(
                        iubendaCookieMatch[1],
                    );

                    const consentData = JSON.parse(cookieValue);

                    // Check if user has accepted Purpose 3 (Experience) needed for videos
                    const hasVideoConsent =
                        consentData.purposes &&
                        consentData.purposes['3'] === true;

                    if (isMounted) {
                        setState((prevState) => {
                            // Only update if the state actually changed to avoid unnecessary re-renders
                            if (
                                prevState.isAccepted !== hasVideoConsent ||
                                prevState.isLoading
                            ) {
                                return {
                                    isAccepted: hasVideoConsent,
                                    isLoading: false,
                                };
                            }

                            return prevState;
                        });
                    }

                    return;
                } catch (error) {
                    // Silent error handling
                }
            }

            // Fallback: if no iubenda cookie found, allow content (user hasn't been asked yet)
            if (isMounted) {
                setState((prevState) => {
                    if (prevState.isAccepted !== true || prevState.isLoading) {
                        return { isAccepted: true, isLoading: false };
                    }

                    return prevState;
                });
            }
        };

        // Listen for iubenda events
        const handleIubendaEvents = () => {
            if (isMounted) {
                checkCookieConsent();
            }
        };

        // Only run initial check if we're still in loading state
        if (state.isLoading) {
            checkCookieConsent();
        }

        // Add event listeners only if window is available
        const eventListeners: Array<{ event: string; handler: () => void }> =
            [];

        if (typeof window !== 'undefined') {
            const events = [
                'iubenda_consent_given',
                'iubenda_consent_rejected',
                'iubenda_preference_expressed',
            ];

            events.forEach((eventName) => {
                window.addEventListener(eventName, handleIubendaEvents);
                eventListeners.push({
                    event: eventName,
                    handler: handleIubendaEvents,
                });
            });
        }

        // Reduced interval frequency to avoid performance issues
        const interval = setInterval(() => {
            if (isMounted) {
                checkCookieConsent();
            }
        }, 5000);

        return () => {
            isMounted = false;
            clearInterval(interval);

            // Safely remove event listeners
            if (typeof window !== 'undefined') {
                eventListeners.forEach(({ event, handler }) => {
                    try {
                        window.removeEventListener(event, handler);
                    } catch (error) {
                        // Silent error handling
                    }
                });
            }
        };
    }, [state.isLoading]);

    return state;
}
